<template>
  <transition name="fading">
    <Loading v-if="isLoading"/>
  </transition>

  <Modal
    :isVisible="modal.deactivateUser"
    promptText="Deactivate this account?"
    yesText="Deactivate"
    noText="Cancel"
    @yes="deactivate_user()"
    @no="close_deactivate()"
  />

  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Account Directory</p>
        <h1 class="page-title">Accounts</h1>
        <p class="page-subtitle">
          Create and manage admin or staff access without mixing profile fields with workspace setup.
        </p>
      </div>
      <button class="ui-button primary" @click="$router.push('/users/register')">
        <i class="fa-solid fa-plus"></i> Create account
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Visible accounts</span>
        <strong class="stat-value">{{ raw_data.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Filtered</span>
        <strong class="stat-value">{{ render_data.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Role scope</span>
        <strong class="stat-value stat-text">{{ roleScopeLabel }}</strong>
      </div>
      <div class="stat-card" v-if="authStore.userRole === 'admin'">
        <span class="stat-label">State admins</span>
        <strong class="stat-value">{{ stateAdminCount }}</strong>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-search">
        <SearchBar
          searchPlaceholder="Search account, email, role, clinic..."
          buttonPlaceholder="Search"
          @OutputSearch="get_search_query"
        />
      </div>
      <div class="role-filter" v-if="authStore.userRole === 'admin'">
        <button
          v-for="option in roleFilterOptions"
          :key="option.value"
          type="button"
          class="role-filter__button"
          :class="{ active: roleFilter === option.value }"
          @click="setRoleFilter(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
      <button class="ui-button secondary" @click="$router.push('/bulk-setup')" v-if="authStore.userRole === 'admin'">
        <i class="fas fa-layer-group"></i> Bulk setup
      </button>
      <button class="ui-button secondary" @click="$router.push('/state-access')" v-if="authStore.userRole === 'admin'">
        <i class="fas fa-eye"></i> State access
      </button>
    </div>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th class="text-start">Account</th>
            <th class="text-start">Role</th>
            <th class="text-start">Assignment</th>
            <th class="text-start">Contact</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="data in paginatedData" :key="data.uuid" @click="$router.push(`/users/edit/${data.uuid}`)" class="clickable-row">
            <td data-label="Account">
              <div class="identity-cell">
                <div class="avatar-circle">{{ firstLetter(data.full_name || data.username) }}</div>
                <div>
                  <span class="table-primary">{{ data.full_name || 'Unnamed account' }}</span>
                  <span class="table-secondary">@{{ data.username || 'no-username' }}</span>
                </div>
              </div>
            </td>
            <td data-label="Role">
              <span class="badge-soft" :class="getRoleBadgeClass(data.role)">
                {{ formatRole(data.role) }}
              </span>
            </td>
            <td data-label="Assignment">
              <span class="table-primary">{{ getAssignmentTitle(data) }}</span>
              <span class="table-secondary">{{ getAssignmentSubtitle(data) }}</span>
            </td>
            <td data-label="Contact">
              <span class="table-primary">{{ data.email || 'No email' }}</span>
              <span class="table-secondary">{{ data.contact_number || 'No phone' }}</span>
            </td>
            <td class="text-end" data-label="Actions">
              <div class="table-actions">
                <button class="icon-button-soft" @click.stop="$router.push(`/users/edit/${data.uuid}`)" title="Edit account">
                  <i class="fas fa-pen"></i>
                </button>
                <button
                  v-if="isStateHealthOrg(data) && getStateHealthOrgUuid(data)"
                  class="icon-button-soft"
                  @click.stop="goToStateAccess(data)"
                  title="Manage state access"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button
                  v-if="authStore.userRole === 'admin'"
                  class="icon-button-soft danger-icon"
                  @click.stop="ask_modal(data.uuid)"
                  title="Deactivate account"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="paginatedData.length === 0">
            <td colspan="5">
              <div class="empty-state">
                <strong>No accounts found.</strong>
                <p>Try adjusting your search or create a new account.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-row" v-if="totalPages > 1">
        <span class="table-secondary">Page {{ current_page }} of {{ totalPages }}</span>
        <div class="table-actions">
          <button class="icon-button-soft" :disabled="current_page === 1" @click="changePage(current_page - 1)">
            <ChevronLeft :size="16" />
          </button>
          <button class="icon-button-soft" :disabled="current_page === totalPages" @click="changePage(current_page + 1)">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/authStore'
import Loading from "@/components/Loading.vue";
import Modal from "@/components/Modal.vue";
import SearchBar from "@/components/SearchBar.vue";
import { getAllUser, getCurrentUser, deactivateUser } from "@/services/userServices"
import { getStateHealthOrgs } from "@/services/stateHealthOrgServices"
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

export default{
  components: {
    Loading,
    Modal,
    SearchBar,
    ChevronLeft,
    ChevronRight,
  },

  created(){
    this.get_data();
  },
  data() {
    return {
      authStore: useAuthStore(),
      raw_data: [],
      render_data: [],
      search_query: '',
      roleFilter: 'all',
      stateHealthOrgs: [],
      current_page: 1,
      page_limit: 10,
      isLoading: false,
      modal: {
        deactivateUser: false,
      },
      hold: {
        user_uuid: '',
      }
    }
  },
  computed: {
    paginatedData() {
      const start = (this.current_page - 1) * this.page_limit;
      return this.render_data.slice(start, start + this.page_limit);
    },
    totalPages() {
      return Math.ceil(this.render_data.length / this.page_limit);
    },
    roleFilterOptions() {
      return [
        { value: 'all', label: 'All admins' },
        { value: 'clinic_admin', label: 'Clinic admins' },
        { value: 'state_health_org', label: 'State admins' },
      ];
    },
    roleScopeLabel() {
      if (this.authStore.userRole === 'admin') {
        return this.roleFilter === 'all' ? 'Clinic + state admins' : this.formatRole(this.roleFilter);
      }
      return 'Staff';
    },
    stateAdminCount() {
      return this.raw_data.filter((user) => user.role === 'state_health_org').length;
    },
    stateHealthOrgByUuid() {
      return this.stateHealthOrgs.reduce((result, org) => {
        result[org.StateHealthOrgUuid] = org;
        return result;
      }, {});
    },
  },
  methods: {
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.current_page = page;
      }
    },
    firstLetter(value) {
      return (value || 'A').charAt(0).toUpperCase();
    },
    formatRole(role = '') {
      return role.replace("_", " ") || 'Unassigned';
    },
    getRoleBadgeClass(role = '') {
      if (role.toLowerCase() === 'admin') return 'danger';
      if (role.toLowerCase() === 'clinic_admin') return 'success';
      if (role.toLowerCase() === 'state_health_org') return 'warning';
      return 'muted';
    },
    isStateHealthOrg(data) {
      return data?.role === 'state_health_org';
    },
    getStateHealthOrgUuid(data) {
      return data?.additional_info?.StateHealthOrgUuid ||
        data?.AdditionalInfo?.StateHealthOrgUuid ||
        data?.state_health_org_uuid ||
        '';
    },
    getStateHealthOrg(data) {
      return this.stateHealthOrgByUuid[this.getStateHealthOrgUuid(data)] || null;
    },
    getAssignmentTitle(data) {
      if (this.isStateHealthOrg(data)) {
        const stateOrg = this.getStateHealthOrg(data);
        return stateOrg?.Name || 'State organization assigned';
      }
      return data.clinic_name || data.OrganizationName || 'No clinic assigned';
    },
    getAssignmentSubtitle(data) {
      if (this.isStateHealthOrg(data)) {
        const stateOrg = this.getStateHealthOrg(data);
        return stateOrg ? `${stateOrg.StateName || 'No state'} watchable clinic access` : 'State organization assignment managed in account';
      }
      return data.OrganizationName || 'Organization assignment managed in account';
    },
    goToStateAccess(data) {
      const stateHealthOrgUuid = this.getStateHealthOrgUuid(data);
      if (stateHealthOrgUuid) {
        this.$router.push({ name: 'state-access-edit', params: { id: stateHealthOrgUuid } });
      }
    },
    setRoleFilter(role) {
      this.roleFilter = role;
      this.applyFilters();
    },
    get_search_query(search_query){
      this.search_query = search_query || '';
      this.applyFilters();
    },
    applyFilters() {
      const query = this.search_query ? this.search_query.toLowerCase() : '';
      this.current_page = 1;
      this.render_data = this.raw_data.filter((data) => {
        if (this.roleFilter !== 'all' && data.role !== this.roleFilter) {
          return false;
        }

        const stateOrg = this.getStateHealthOrg(data);
        return [
          data.username,
          data.role,
          data.clinic_name,
          data.full_name,
          data.email,
          data.contact_number,
          data.OrganizationName,
          stateOrg?.Name,
          stateOrg?.StateName,
        ].some((value) => String(value || '').toLowerCase().includes(query));
      });
    },
    async get_data(){
      this.isLoading = true
      if (this.authStore.userRole === 'admin') {
        try {
          const stateOrgResult = await getStateHealthOrgs();
          this.stateHealthOrgs = stateOrgResult.data || [];
        } catch (error) {
          this.stateHealthOrgs = [];
        }
      }

      const result = await getAllUser();

      if(result.users){
        let users = this.authStore.userRole === 'admin'
          ? result.users.filter(user => ['clinic_admin', 'state_health_org'].includes(user.role))
          : result.users.filter(user => user.role === 'user');

        if (this.authStore.userRole === 'admin') {
          users = await Promise.all(users.map(async (user) => {
            if (user.role !== 'state_health_org' || user.additional_info?.StateHealthOrgUuid) {
              return user;
            }

            try {
              const detail = await getCurrentUser(user.uuid);
              return {
                ...user,
                additional_info: detail.user_info?.AdditionalInfo || user.additional_info || {},
              };
            } catch (error) {
              return user;
            }
          }));
        }

        this.raw_data = [...users].reverse();
        this.applyFilters();
      }
      this.isLoading = false;
    },
    ask_modal(uuid){
      this.hold.user_uuid = uuid;
      this.modal.deactivateUser = true
    },
    async deactivate_user(){
      await deactivateUser(this.hold.user_uuid);
      this.modal.deactivateUser = false;
      this.get_data();
    },
    close_deactivate(){
      this.modal.deactivateUser = false;
    },
  }
}
</script>

<style scoped>
.identity-cell,
.table-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-actions {
  justify-content: flex-end;
}

.stats-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.role-filter {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--ui-border);
  border-radius: 10px;
  background: #f8fafc;
}

.role-filter__button {
  min-height: 34px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--ui-muted);
  padding: 0 12px;
  font-size: 0.82rem;
  font-weight: 700;
}

.role-filter__button.active {
  background: #ffffff;
  color: var(--primary-dark);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.avatar-circle {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #f0e5f6;
  color: var(--primary-dark);
  font-weight: 800;
}

.clickable-row {
  cursor: pointer;
}

.danger-icon {
  color: var(--error-color);
}

.danger-icon:hover {
  background: var(--error-color);
}

.stat-text {
  font-size: 1rem;
}

@media (max-width: 900px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .role-filter {
    width: 100%;
    justify-content: stretch;
  }

  .role-filter__button {
    flex: 1;
  }
}
</style>
